// 용량 옵션 관리 훅 — 관리자 설정(device_catalog.storageOptions)만 노출 (SSOT)
import { useMemo, useCallback } from 'react';
import { getStorageOptionsWithStatus } from '../utils/storageDisplayUtils';

interface SmartStorageOptionsParams {
  deviceType: 'smartphone' | 'tablet';
  brand: 'Apple' | 'Samsung' | 'LG';
  selectedModel: string;
  adminStorageOptions: string[];
  allPrices?: any[]; // 가격 데이터 (옵션)
}

export const useSmartStorageOptions = ({
  deviceType,
  brand, 
  selectedModel,
  adminStorageOptions,
  allPrices = []
}: SmartStorageOptionsParams) => {
  
  // 🔍 용량별 가격 상태 확인 (안전한 useCallback)
  const getEstimatedPrice = useCallback((storage: string, prices: any[]): number | null => {
    if (!prices.length) return null;
    
    const matchingPrice = prices.find((price: any) => 
      price.variant?.storage === storage
    );
    
    return matchingPrice?.gradePrices?.A || null;
  }, []);
  
  // 💰 가격 상태 분류 (안전한 useCallback)
  const getPriceStatus = useCallback((storage: string, prices: any[], tempStorageOptions: any[]): 'set' | 'estimated' | 'consultation' => {
    const estimatedPrice = getEstimatedPrice(storage, prices);
    
    if (estimatedPrice && estimatedPrice > 0) {
      return 'set'; // 정확한 가격 설정됨
    }
    
    // 표준 용량이면 예상 가격 제공, 아니면 상담 안내
    const isStandard = tempStorageOptions.find((opt: any) => opt.storage === storage)?.isStandard;
    return isStandard ? 'estimated' : 'consultation';
  }, [getEstimatedPrice]);
  
  // 🎯 용량 옵션 생성 (관리자 설정 용량만 — 관리자 페이지와 동일 목록)
  const storageOptionsWithStatus = useMemo(() => {
    const options = getStorageOptionsWithStatus(
      deviceType,
      brand,
      adminStorageOptions
    );
    
    // 각 용량별 예상 가격 정보 추가
    return options.map(option => ({
      ...option,
      estimatedPrice: getEstimatedPrice(option.storage, allPrices),
      priceStatus: getPriceStatus(option.storage, allPrices, options)
    }));
  }, [deviceType, brand, adminStorageOptions, allPrices, selectedModel]);
  
  // 📊 통계 정보
  const statistics = useMemo(() => {
    const totalOptions = storageOptionsWithStatus.length;
    const priceSetOptions = storageOptionsWithStatus.filter(opt => opt.hasAdminPrice).length;
    const standardOptions = storageOptionsWithStatus.filter(opt => opt.isStandard).length;
    const specialOptions = storageOptionsWithStatus.filter(opt => !opt.isStandard).length;
    
    return {
      totalOptions,
      priceSetOptions,
      standardOptions,
      specialOptions,
      coverageRate: totalOptions > 0 ? Math.round((priceSetOptions / totalOptions) * 100) : 0
    };
  }, [storageOptionsWithStatus]);
  
  // 🎨 카테고리별 분류 (UI 그룹핑용)
  const categorizedOptions = useMemo(() => {
    const priceSet = storageOptionsWithStatus.filter(opt => opt.hasAdminPrice);
    const standard = storageOptionsWithStatus.filter(opt => !opt.hasAdminPrice && opt.isStandard);
    const special = storageOptionsWithStatus.filter(opt => !opt.hasAdminPrice && !opt.isStandard);
    
    return {
      priceSet,    // 가격 설정된 용량들 (우선 표시)
      standard,    // 표준 용량들 (두 번째 표시)  
      special      // 특별 용량들 (마지막 표시)
    };
  }, [storageOptionsWithStatus]);
  
  return {
    storageOptionsWithStatus,
    categorizedOptions,
    statistics,
    // 헬퍼 함수들
    getEstimatedPrice,
    getPriceStatus
  };
};
